import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Vote, 
  Search,
  Filter,
  Eye,
  Calendar,
  Building2,
  CheckCircle2,
  Key,
  Copy,
  Check,
  BarChart3,
  Trash2,
  Loader2,
  Play,
  XCircle,
  Pencil,
  Save,
  ExternalLink,
  Zap
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/AdminLayout";
import { getAdminConsultes, getAdminConsultaDetail, requestDeletion, adminActivateConsulta, adminRejectConsulta, adminUpdateConsulta, adminCloseConsulta, adminRejectClosure, adminSimulateVotes } from "../../lib/adminApi";
import { toast } from "sonner";

export default function AdminConsultesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [consultes, setConsultes] = useState([]);
  const [filteredConsultes, setFilteredConsultes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  // Simulate votes state
  const [simNumVotes, setSimNumVotes] = useState(100);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => { loadConsultes(); }, []);
  useEffect(() => { filterConsultes(); }, [searchTerm, statusFilter, consultes]);

  // Auto-open modal if navigated with openConsultaId from dashboard
  useEffect(() => {
    if (location.state?.openConsultaId && consultes.length > 0) {
      const c = consultes.find(x => x.id === location.state.openConsultaId);
      if (c) handleViewDetail(c);
      window.history.replaceState({}, '');
    }
  }, [consultes, location.state]);

  const loadConsultes = async () => {
    try {
      const response = await getAdminConsultes();
      setConsultes(response.data);
    } catch (error) {
      toast.error(t('consultes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filterConsultes = () => {
    let filtered = consultes;
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.entity_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== "all") {
      if (statusFilter === "scrutiny_requested") {
        filtered = filtered.filter(c => c.status === "closed" && c.scrutiny_request_status === "pending");
      } else if (statusFilter === "pending_entity_key") {
        filtered = filtered.filter(c => c.status === "closed" && c.scrutiny_request_status === "admin_approved");
      } else if (statusFilter === "voting") {
        filtered = filtered.filter(c => c.status === "published" && c.start_date && c.end_date &&
          new Date() >= new Date(c.start_date) && new Date() <= new Date(c.end_date));
      } else {
        filtered = filtered.filter(c => c.status === statusFilter);
      }
    }
    setFilteredConsultes(filtered);
  };

  const handleViewDetail = async (consulta) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    setEditing(false);
    try {
      const response = await getAdminConsultaDetail(consulta.id);
      setSelectedConsulta(response.data);
    } catch (error) {
      toast.error(t('adminConsultes.loadDetailsError'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStartEdit = () => {
    setEditing(true);
    setEditData({
      title: selectedConsulta.title,
      description: selectedConsulta.description || "",
      start_date: selectedConsulta.start_date ? selectedConsulta.start_date.slice(0, 16) : "",
      end_date: selectedConsulta.end_date ? selectedConsulta.end_date.slice(0, 16) : "",
      voting_restriction: selectedConsulta.voting_restriction || "none",
      min_options: selectedConsulta.min_options || 1,
      max_options: selectedConsulta.max_options || 1,
      max_amount: selectedConsulta.max_amount || "",
      require_registration: selectedConsulta.require_registration ?? false,
      enable_private_audit: selectedConsulta.enable_private_audit ?? false,
      enable_vot_ma_alcada: selectedConsulta.enable_vot_ma_alcada ?? false,
      enable_audit: selectedConsulta.enable_audit ?? false,
      enable_voter_tracking: selectedConsulta.enable_voter_tracking ?? false,
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await adminUpdateConsulta(selectedConsulta.id, editData);
      toast.success(t('consultaDetail.updated'));
      setEditing(false);
      const response = await getAdminConsultaDetail(selectedConsulta.id);
      setSelectedConsulta(response.data);
      loadConsultes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('consultaDetail.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (consultaId) => {
    if (!window.confirm(t('adminConsultes.confirmActivate'))) return;
    setActivatingId(consultaId);
    try {
      await adminActivateConsulta(consultaId);
      toast.success(t('adminConsultes.activated'));
      setShowDetailModal(false);
      loadConsultes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('consultaDetail.activateError'));
    } finally {
      setActivatingId(null);
    }
  };

  const handleReject = async (consultaId) => {
    if (!window.confirm(t('adminConsultes.confirmReject'))) return;
    try {
      await adminRejectConsulta(consultaId);
      toast.success(t('adminConsultes.returnedToDraft'));
      setShowDetailModal(false);
      loadConsultes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminConsultes.rejectError'));
    }
  };

  const handleApproveClosure = async (consultaId) => {
    if (!window.confirm(t('adminConsultes.confirmApproveClosure'))) return;
    setActivatingId(consultaId);
    try {
      await adminCloseConsulta(consultaId);
      toast.success(t('adminConsultes.closed'));
      setShowDetailModal(false);
      loadConsultes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminConsultes.closeError'));
    } finally {
      setActivatingId(null);
    }
  };

  const handleRejectClosure = async (consultaId) => {
    if (!window.confirm(t('adminConsultes.confirmRejectClosure'))) return;
    try {
      await adminRejectClosure(consultaId);
      toast.success(t('adminConsultes.closeRejected'));
      setShowDetailModal(false);
      loadConsultes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminConsultes.closeRejectError'));
    }
  };

  const handleDeleteConsulta = async (consulta) => {
    if (!window.confirm(t('adminConsultes.confirmDelete', { title: consulta.title }))) return;
    setDeletingId(consulta.id);
    try {
      await requestDeletion("votació", consulta.id);
      toast.success(t('adminConsultes.confirmEmailSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.deleteRequestError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSimulateVotes = async () => {
    if (!selectedConsulta || simulating) return;
    setSimulating(true);
    try {
      const response = await adminSimulateVotes(selectedConsulta.id, simNumVotes);
      toast.success(response.data.message);
      // Refresh detail
      const detailRes = await getAdminConsultaDetail(selectedConsulta.id);
      setSelectedConsulta(detailRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error simulant vots");
    } finally {
      setSimulating(false);
    }
  };

  const pendingCount = consultes.filter(c => c.status === "pending_publication").length;

  const getStatusBadge = (status, consulta) => {
    const isVoting = status === "published" && consulta?.start_date && consulta?.end_date &&
      new Date() >= new Date(consulta.start_date) && new Date() <= new Date(consulta.end_date);

    if (isVoting) {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-emerald-100">{t('adminConsultes.voting')}</span>;
    }

    if (status === "pending_blockchain") {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-500 text-white" data-testid="status-pending-blockchain">{t('status.pendingBlockchain')}</span>;
    }

    if (status === "closed" && consulta?.scrutiny_request_status === "admin_approved") {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-emerald-100" data-testid="status-pending-entity-key">{t('status.pendingEntityKey')}</span>;
    }

    if (status === "closed" && consulta?.scrutiny_request_status) {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-indigo-100" data-testid="status-scrutiny-requested">{t('status.scrutinyRequested')}</span>;
    }

    const badges = {
      draft: { class: "bg-slate-600 text-slate-200", label: t('status.draft') },
      pending_publication: { class: "bg-orange-500 text-white", label: t('status.pendingPublication') },
      published: { class: "bg-blue-500 text-white", label: t('status.published') },
      pending_closure: { class: "bg-red-500 text-white", label: t('status.pendingClosure') },
      closed: { class: "bg-amber-600 text-amber-100", label: t('status.closed') },
      expired: { class: "bg-slate-400 text-white", label: t('status.expired') },
      scrutinized: { class: "bg-blue-600 text-blue-100", label: t('status.scrutinized') }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>{badge.label}</span>;
  };

  const formatDt = (iso, tz) => {
    if (!iso) return "—";
    // Handle both UTC (old dates with Z) and local (new dates without Z)
    const s = String(iso).trim();
    const isUTC = s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s);
    if (isUTC) {
      let ts = s;
      if (!ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) ts += 'Z';
      const localeMap = { ca: "ca-ES", es: "es-ES", eu: "eu-ES", gl: "gl-ES" };
      const loc = localeMap[i18n.language] || "ca-ES";
      return new Date(ts).toLocaleString(loc, { timeZone: tz || "Europe/Madrid", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
    const parts = s.split('T');
    if (parts.length < 2) return iso;
    const [year, month, day] = parts[0].split('-');
    const [hour, minute] = parts[1].split(':');
    return `${(day||'').padStart(2,'0')}/${(month||'').padStart(2,'0')}/${year} ${(hour||'00').padStart(2,'0')}:${(minute||'00').padStart(2,'0')}`;
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-consultes-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-heading font-bold text-white">{t('adminConsultes.title')}</h1>
            {pendingCount > 0 && (
              <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-bold animate-pulse" data-testid="pending-count-badge">
                {pendingCount} pendent{pendingCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <p className="text-slate-400 mt-1">{t('adminConsultes.allConsultes')}</p>
        </motion.div>

        {/* Filters */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder={t('admin.searchConsultesEntities')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="admin-search-consultes"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-56 bg-slate-800 border-slate-700 text-white" data-testid="admin-status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('adminConsultes.filterPlaceholder')} />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">{t('adminConsultes.allStatuses')}</SelectItem>
              <SelectItem value="draft" className="text-white">{t('status.draft')}</SelectItem>
              <SelectItem value="pending_publication" className="text-orange-400">{t('status.pendingPublication')}</SelectItem>
              <SelectItem value="published" className="text-blue-400">{t('status.published')}</SelectItem>
              <SelectItem value="voting" className="text-emerald-400">{t('status.voting')}</SelectItem>
              <SelectItem value="pending_closure" className="text-red-400">{t('status.pendingClosure')}</SelectItem>
              <SelectItem value="closed" className="text-amber-400">{t('status.closed')}</SelectItem>
              <SelectItem value="pending_blockchain" className="text-orange-400">{t('status.pendingBlockchain')}</SelectItem>
              <SelectItem value="scrutiny_requested" className="text-indigo-400">{t('status.scrutinyRequested')}</SelectItem>
              <SelectItem value="pending_entity_key" className="text-emerald-400">{t('status.pendingEntityKey')}</SelectItem>
              <SelectItem value="scrutinized" className="text-blue-400">{t('status.scrutinized')}</SelectItem>
              <SelectItem value="expired" className="text-slate-400">{t('status.expired')}</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Consultes List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredConsultes.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16 bg-slate-800 border border-slate-700 rounded-lg">
            <Vote className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">{t('adminConsultes.noConsultes')}</h3>
            <p className="text-slate-400">
              {searchTerm || statusFilter !== "all" ? t('adminConsultes.noConsultesFiltered') : t('adminConsultes.noConsultesYet')}
            </p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">{t('adminConsultes.consultaColumn')}</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">{t('adminConsultes.entityColumn')}</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">{t('adminConsultes.datesColumn')}</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">{t('adminConsultes.statusColumn')}</th>
                    <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider py-4 px-6">{t('adminConsultes.actionsColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConsultes.map((consulta) => (
                    <tr 
                      key={consulta.id} 
                      className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors cursor-pointer ${consulta.status === "pending_publication" ? "bg-orange-500/5" : ""}`}
                      data-testid={`admin-consulta-row-${consulta.id}`}
                      onClick={() => handleViewDetail(consulta)}
                    >
                      <td className="py-4 px-6">
                        <p className="font-medium text-white">{consulta.title}</p>
                        <p className="text-sm text-slate-400 truncate max-w-xs">{consulta.description}</p>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Building2 className="h-4 w-4 text-slate-500" />
                          {consulta.entity_name}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                          <Calendar className="h-4 w-4 text-slate-500" />
                          {formatDt(consulta.start_date, consulta.entity_timezone)} - {formatDt(consulta.end_date, consulta.entity_timezone)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {getStatusBadge(consulta.status, consulta)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          {consulta.status === "pending_publication" && (
                            <Button
                              size="sm"
                              onClick={() => handleActivate(consulta.id)}
                              disabled={activatingId === consulta.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`admin-activate-${consulta.id}`}
                            >
                              {activatingId === consulta.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1" />{t('adminConsultes.publish')}</>}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConsulta(consulta)}
                            disabled={deletingId === consulta.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`admin-delete-consulta-${consulta.id}`}
                          >
                            {deletingId === consulta.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" />{t('adminConsultes.delete')}</>}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setEditing(false); }}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('adminConsultes.consultaDetails')}</DialogTitle>
            <DialogDescription className="text-slate-400 sr-only">{selectedConsulta?.title || ''}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedConsulta && (
            <div className="space-y-6">
              {/* Title & Description */}
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300 text-sm">{t('adminConsultes.titleLabel')}</Label>
                    <Input value={editData.title} onChange={(e) => setEditData({...editData, title: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">{t('adminConsultes.descriptionLabel')}</Label>
                    <Textarea value={editData.description} onChange={(e) => setEditData({...editData, description: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminConsultes.startDateLabel')}</Label>
                      <Input type="datetime-local" value={editData.start_date} onChange={(e) => setEditData({...editData, start_date: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminConsultes.endDateLabel')}</Label>
                      <Input type="datetime-local" value={editData.end_date} onChange={(e) => setEditData({...editData, end_date: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                    </div>
                  </div>

                  {/* Advanced Options */}
                  <div className="border border-slate-600 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-semibold text-slate-300">{t('adminConsultes.advancedOptions')}</h4>
                    
                    <div>
                      <Label className="text-slate-300 text-sm">{t('adminConsultes.votingRestriction')}</Label>
                      <Select value={editData.voting_restriction} onValueChange={(v) => setEditData({...editData, voting_restriction: v})}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="admin-edit-voting-restriction">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="none" className="text-white">{t('adminConsultes.noRestriction')}</SelectItem>
                          <SelectItem value="options" className="text-white">{t('adminConsultes.byOptions')}</SelectItem>
                          <SelectItem value="amount" className="text-white">{t('adminConsultes.byAmount')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {editData.voting_restriction === "options" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300 text-sm">{t('adminConsultes.minOptions')}</Label>
                          <Input type="number" min={1} value={editData.min_options} onChange={(e) => setEditData({...editData, min_options: parseInt(e.target.value) || 1})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-sm">{t('adminConsultes.maxOptions')}</Label>
                          <Input type="number" min={1} value={editData.max_options} onChange={(e) => setEditData({...editData, max_options: parseInt(e.target.value) || 1})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                        </div>
                      </div>
                    )}

                    {editData.voting_restriction === "amount" && (
                      <div>
                        <Label className="text-slate-300 text-sm">{t('adminConsultes.maxAmount')}</Label>
                        <Input type="number" min={0} step="0.01" value={editData.max_amount} onChange={(e) => setEditData({...editData, max_amount: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600 text-white mt-1" />
                      </div>
                    )}

                    <div className="space-y-3">
                      {[
                        { key: "require_registration", label: t('adminConsultes.requireRegistration') },
                        { key: "enable_private_audit", label: t('adminConsultes.privateAudit'),
                          disabled: editData.enable_vot_ma_alcada || editData.enable_audit },
                        { key: "enable_vot_ma_alcada", label: t('adminConsultes.handVote'),
                          disabled: editData.enable_audit || editData.enable_private_audit || editData.enable_voter_tracking },
                        { key: "enable_audit", label: t('adminConsultes.participationControl'),
                          disabled: editData.enable_vot_ma_alcada || editData.enable_private_audit },
                        { key: "enable_voter_tracking", label: t('adminConsultes.voterTracking'),
                          disabled: editData.enable_vot_ma_alcada },
                      ].map(({ key, label, disabled }) => (
                        <label key={key} className={`flex items-center gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={editData[key] || false}
                            disabled={disabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates = { [key]: checked };
                              if (checked) {
                                if (key === 'enable_vot_ma_alcada') { updates.enable_audit = false; updates.enable_private_audit = false; updates.enable_voter_tracking = false; }
                                if (key === 'enable_audit') { updates.enable_vot_ma_alcada = false; updates.enable_private_audit = false; }
                                if (key === 'enable_private_audit') { updates.enable_vot_ma_alcada = false; updates.enable_audit = false; }
                                if (key === 'enable_voter_tracking') { updates.enable_vot_ma_alcada = false; }
                              }
                              setEditData({...editData, ...updates});
                            }}
                            className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                            data-testid={`admin-edit-${key}`}
                          />
                          <span className="text-sm text-slate-300">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-save-edit-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      {t('adminConsultes.save')}
                    </Button>
                    <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">{t('common.cancel')}</Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{selectedConsulta.title}</h3>
                      <p className="text-slate-400">{selectedConsulta.description}</p>
                    </div>
                    {(selectedConsulta.status === "pending_publication" || selectedConsulta.status === "draft") && (
                      <Button variant="ghost" size="sm" onClick={handleStartEdit} className="text-slate-400 hover:text-white" data-testid="admin-edit-btn">
                        <Pencil className="h-4 w-4 mr-1" />{t('adminConsultes.edit')}
                      </Button>
                    )}
                    {selectedConsulta.status === "closed" && selectedConsulta.scrutiny_request_status === "pending" && selectedConsulta.scrutiny_request_token && (
                      <Button
                        size="sm"
                        onClick={() => { setShowDetailModal(false); navigate(`/admin/scrutiny/${selectedConsulta.scrutiny_request_token}`); }}
                        className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
                        data-testid="admin-approve-scrutiny-btn"
                      >
                        <Key className="h-4 w-4 mr-1" />
                        {t('adminConsultes.approveScrutiny')}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.entityLabel')}</p>
                  <p className="text-white font-medium">{selectedConsulta.entity_name}</p>
                  <p className="text-sm text-slate-400">{selectedConsulta.entity_email}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.statusLabel')}</p>
                  {getStatusBadge(selectedConsulta.status, selectedConsulta)}
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.totalCensus')}</p>
                  <p className="text-2xl font-bold text-white">{selectedConsulta.census_total}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.haveVoted')}</p>
                  <p className="text-2xl font-bold text-emerald-400">{selectedConsulta.census_voted}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.startDateLabel')}</p>
                  <p className="text-white font-medium">{formatDt(selectedConsulta.start_date)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">{t('adminConsultes.endDateLabel')}</p>
                  <p className="text-white font-medium">{formatDt(selectedConsulta.end_date)}</p>
                </div>
              </div>

              {/* Voting Link */}
              <div className="bg-slate-700/50 rounded-lg p-4" data-testid="admin-voting-link">
                <p className="text-sm text-slate-400 mb-2">{t('adminConsultes.votingLink')}</p>
                <div className="p-2 bg-slate-800 rounded border border-slate-600 font-mono text-sm text-blue-400 break-all mb-2">
                  {`${window.location.origin}/vote/${selectedConsulta.id}`}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/vote/${selectedConsulta.id}`);
                      toast.success(t('common.linkCopied'));
                    }}
                    className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                    data-testid="admin-copy-voting-link"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    {t('adminConsultes.copy')}
                  </Button>
                  <a href={`${window.location.origin}/vote/${selectedConsulta.id}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700" data-testid="admin-open-voting-link">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {t('adminConsultes.open')}
                    </Button>
                  </a>
                </div>
              </div>

              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">{t('adminConsultes.votingOptions')}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedConsulta.options?.map((option, i) => {
                    const optionTitle = typeof option === 'string' ? option : option.title;
                    return (
                      <span key={i} className="px-3 py-1 bg-slate-600 rounded-full text-sm text-white">{optionTitle}</span>
                    );
                  })}
                </div>
              </div>

              {/* Advanced options display */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2" data-testid="admin-advanced-options-display">
                <p className="text-sm text-slate-400 mb-2">{t('adminConsultes.advancedOptions')}</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${selectedConsulta.voting_restriction && selectedConsulta.voting_restriction !== "none" ? "bg-emerald-400" : "bg-slate-500"}`} />
                    <span className="text-slate-300">{t('adminConsultes.restriction')}: {selectedConsulta.voting_restriction === "options" ? t('adminConsultes.byOptionsLabel') : selectedConsulta.voting_restriction === "amount" ? t('adminConsultes.byAmountLabel') : t('adminConsultes.none')}</span>
                  </div>
                  {selectedConsulta.voting_restriction === "options" && (
                    <>
                      <div className="text-slate-400">{t('adminConsultes.minOptions')}: {selectedConsulta.min_options || 1} / {t('adminConsultes.maxOptions')}: {selectedConsulta.max_options || 1}</div>
                    </>
                  )}
                  {selectedConsulta.voting_restriction === "amount" && (
                    <div className="text-slate-400">{t('adminConsultes.maxAmount')}: {selectedConsulta.max_amount || 0}</div>
                  )}
                  {[
                    { key: "require_registration", label: t('adminConsultes.requireRegistration') },
                    { key: "enable_private_audit", label: t('adminConsultes.privateAudit') },
                    { key: "enable_vot_ma_alcada", label: t('adminConsultes.handVote') },
                    { key: "enable_audit", label: t('adminConsultes.participationControl') },
                    { key: "enable_voter_tracking", label: t('adminConsultes.voterTracking') },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${selectedConsulta[key] ? "bg-emerald-400" : "bg-slate-500"}`} />
                      <span className="text-slate-300">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Simulate Votes - DISABLED for production */}

              {/* Action buttons based on status */}
              {selectedConsulta.status === "pending_publication" && (
                <div className="flex gap-3" data-testid="admin-pending-actions">
                  <Button
                    onClick={() => handleActivate(selectedConsulta.id)}
                    disabled={activatingId === selectedConsulta.id}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="admin-activate-detail-btn"
                  >
                    {activatingId === selectedConsulta.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    {t('adminConsultes.activateConsulta')}
                  </Button>
                  <Button
                    onClick={() => handleReject(selectedConsulta.id)}
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="admin-reject-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {t('adminConsultes.reject')}
                  </Button>
                </div>
              )}

              {selectedConsulta.status === "pending_closure" && (
                <div className="flex gap-3" data-testid="admin-pending-closure-actions">
                  <Button
                    onClick={() => handleApproveClosure(selectedConsulta.id)}
                    disabled={activatingId === selectedConsulta.id}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="admin-approve-closure-detail-btn"
                  >
                    {activatingId === selectedConsulta.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {t('adminConsultes.approveClosure')}
                  </Button>
                  <Button
                    onClick={() => handleRejectClosure(selectedConsulta.id)}
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="admin-reject-closure-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {t('adminConsultes.rejectClosure')}
                  </Button>
                </div>
              )}

              {selectedConsulta.status === "closed" && selectedConsulta.scrutiny_request_status === "admin_approved" && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-400">{t('adminConsultes.waitingEntity')}</p>
                </div>
              )}

              {selectedConsulta.status === "scrutinized" && (
                <div className="space-y-3">
                  <Button
                    onClick={() => { setShowDetailModal(false); navigate(`/admin/results/${selectedConsulta.id}`); }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="admin-view-results-btn"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    {t('adminConsultes.viewResultsStats')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
